import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import MenuItem from '@mui/material/MenuItem';

import { selectCompanySettings } from 'modules/settings/stores/company-settings-slice-handler';
import { selectSettingsJson, updateJsonSetting } from 'modules/settings/stores/user-settings-slice-handler';
import t from 'utils/translate';
import LabelWithInfo from 'modules/shared/components/label-with-info';
import SelectInput from 'modules/shared/components/select-input';
import ActiveInactiveSelector from './active-inactive-selector';

export default function SectionNotificationsAndAlerts() {
    const companySettings = useSelector(selectCompanySettings);
    const settingsJson = useSelector(selectSettingsJson);
    const [notificationsAlerts, updateNotificationsAlerts] = useState({
        messageSound: settingsJson.messageSound,
        myBusinessNotificationSound: settingsJson.myBusinessNotificationSound,
        voipCallSound: settingsJson.voipCallSound,
        pttCallSound: settingsJson.pttCallSound,
        messageRead: settingsJson.messageRead,
    });

    const successNotifications = {
        messageSound: t('js_all_message_sound_changed_successfully'),
        myBusinessNotificationSound: t('js_all_my_business_sound_changed_successfully'),
        voipCallSound: t('js_all_voip_ring_tone_sound_changed_successfully'),
        pttCallSound: t('js_all_ptt_ring_tone_sound_changed_successfully'),
        messageRead: t('js_all_user_availability_message_read_confirmation_successfully_changed'),
    };

    const notificationSoundOptions = [
        { value: '__NO_SOUND__', label: 'global_notification_sound___NO_SOUND__' },
        {
            value: 'beer_can_opening',
            label: 'global_notification_sound_beer_can_opening',
        },
        { value: 'bell_ring', label: 'global_notification_sound_bell_ring' },
        { value: 'branch_break', label: 'global_notification_sound_branch_break' },
        { value: 'button_click', label: 'global_notification_sound_button_click' },
        {
            value: 'button_click_on',
            label: 'global_notification_sound_button_click_on',
        },
        { value: 'button_push', label: 'global_notification_sound_button_push' },
        { value: 'button_tiny', label: 'global_notification_sound_button_tiny' },
        {
            value: 'camera_flashing',
            label: 'global_notification_sound_camera_flashing',
        },
        {
            value: 'camera_flashing_2',
            label: 'global_notification_sound_camera_flashing_2',
        },
        { value: 'cd_tray', label: 'global_notification_sound_cd_tray' },
        {
            value: 'computer_error',
            label: 'global_notification_sound_computer_error',
        },
        { value: 'door_bell', label: 'global_notification_sound_door_bell' },
        { value: 'door_bump', label: 'global_notification_sound_door_bump' },
        { value: 'glass', label: 'global_notification_sound_glass' },
        {
            value: 'keyboard_desk',
            label: 'global_notification_sound_keyboard_desk',
        },
        {
            value: 'light_bulb_breaking',
            label: 'global_notification_sound_light_bulb_breaking',
        },
        { value: 'metal_plate', label: 'global_notification_sound_metal_plate' },
        {
            value: 'metal_plate_2',
            label: 'global_notification_sound_metal_plate_2',
        },
        { value: 'pop_cork', label: 'global_notification_sound_pop_cork' },
        { value: 'snap', label: 'global_notification_sound_snap' },
        { value: 'staple_gun', label: 'global_notification_sound_staple_gun' },
        { value: 'tap', label: 'global_notification_sound_tap' },
        {
            value: 'water_droplet',
            label: 'global_notification_sound_water_droplet',
        },
        {
            value: 'water_droplet_2',
            label: 'global_notification_sound_water_droplet_2',
        },
        {
            value: 'water_droplet_3',
            label: 'global_notification_sound_water_droplet_3',
        },
    ];

    const callSoundOptions = [
        { value: '__NO_SOUND__', label: 'global_notification_sound___NO_SOUND__' },
        {
            value: 'ring_tone_ascending_bell_arpeggios',
            label: 'ring_tone_ascending_bell_arpeggios',
        },
        {
            value: 'ring_tone_ascending_bells_and_strings',
            label: 'ring_tone_ascending_bells_and_strings',
        },
        {
            value: 'ring_tone_electronic_telephone_ring',
            label: 'ring_tone_electronic_telephone_ring',
        },
        { value: 'ring_tone_fanta', label: 'ring_tone_fanta' },
        {
            value: 'ring_tone_generic_ringtone',
            label: 'ring_tone_generic_ringtone',
        },
        {
            value: 'ring_tone_low_beep_with_echo',
            label: 'ring_tone_low_beep_with_echo',
        },
        { value: 'ring_tone_simple_ringtone', label: 'ring_tone_simple_ringtone' },
        { value: 'ring_tone_standard', label: 'ring_tone_standard' },
        {
            value: 'ring_tone_telephone_bell_ringing',
            label: 'ring_tone_telephone_bell_ringing',
        },
        { value: 'ring_tone_text_beep', label: 'ring_tone_text_beep' },
        { value: 'ring_tone_trilla_1', label: 'ring_tone_trilla_1' },
        { value: 'ring_tone_trilla_2', label: 'ring_tone_trilla_2' },
    ];

    const handleChange = (field, value) => {
        updateNotificationsAlerts({ ...notificationsAlerts, [field]: value });
        updateJsonSetting(field, value, t('settings_interface_notifications'), successNotifications[field]);
    };

    return (
        <section className="settings-section" data-test-id="notifications-and-alerts">
            <div className="settings-section-title">
                <LabelWithInfo text={t('settings_interface_notifications')} />
                <div className="strike" />
            </div>
            <div className="section-content">
                <div className="section-row">
                    <LabelWithInfo
                        text={t('settings_notification_sound_title')}
                        info={t('settings_notification_sound_tooltip')}
                    />
                    <SelectInput
                        value={notificationsAlerts.messageSound}
                        onChange={(e) => handleChange('messageSound', e.target.value)}
                        className="info-field-dropdown"
                        data-test-id="message-sound"
                    >
                        {notificationSoundOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {t(option.label)}
                            </MenuItem>
                        ))}
                    </SelectInput>
                </div>
                {companySettings.AllowMyBusiness && (
                    <div className="section-row">
                        <LabelWithInfo
                            text={t('settings_business_notification_sound_title')}
                            info={t('settings_business_notification_sound_tooltip')}
                        />
                        <SelectInput
                            value={notificationsAlerts.myBusinessNotificationSound}
                            onChange={(e) => handleChange('myBusinessNotificationSound', e.target.value)}
                            className="info-field-dropdown"
                            data-test-id="my-business-notification-sound"
                        >
                            {notificationSoundOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </SelectInput>
                    </div>
                )}
                {companySettings.AllowVoip && (
                    <div className="section-row">
                        <LabelWithInfo text={t('settings_voip_sound_title')} info={t('settings_voip_sound_tooltip')} />
                        <SelectInput
                            value={notificationsAlerts.voipCallSound}
                            onChange={(e) => handleChange('voipCallSound', e.target.value)}
                            className="info-field-dropdown"
                            data-test-id="voip-call-sound"
                        >
                            {callSoundOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </SelectInput>
                    </div>
                )}
                {companySettings.AllowWalkieTalkie && (
                    <div className="section-row">
                        <LabelWithInfo
                            text={t('settings_walkie_talkie_sound_title')}
                            info={t('settings_walkie_talkie_sound_tooltip')}
                        />
                        <SelectInput
                            value={notificationsAlerts.pttCallSound}
                            onChange={(e) => handleChange('pttCallSound', e.target.value)}
                            className="info-field-dropdown"
                            data-test-id="ptt-call-sound"
                        >
                            {callSoundOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </SelectInput>
                    </div>
                )}
                <div className="section-row">
                    <LabelWithInfo
                        text={t('settings_message_read_notification_title')}
                        info={t('settings_message_read_notification_tooltip')}
                    />
                    <ActiveInactiveSelector
                        initialValue={notificationsAlerts.messageRead}
                        onChange={(v) => handleChange('messageRead', v)}
                        data-test-id="message-read"
                    />
                </div>
            </div>
        </section>
    );
}
